import { useCallback, useEffect, useId, useState } from "react";
import { annualDistance, compute, convertData, newProfile, newVehicle } from "./calc";
import { VEHICLE_COLOR_COUNT } from "./constants";
import { CURRENCY_LABELS, CURRENCY_SYMBOLS, UNIT_LABELS, UNIT_SHORT, footerNote, header, sections, tabs } from "./content";
import { Compare } from "./components/Compare";
import { useDataTransfer } from "./components/DataTransfer";
import { Logo } from "./components/Logo";
import { ProfilePanel } from "./components/ProfilePanel";
import { Results } from "./components/Results";
import { TabBar } from "./components/TabBar";
import { ThemeToggle } from "./components/ThemeToggle";
import { VehicleForm } from "./components/VehicleForm";
import { FmtProvider, makeFmt } from "./fmt";
import { loadData, saveData } from "./storage";
import type { Currency, Data, DistanceUnit, Profile, Vehicle } from "./types";

const CURRENCIES: Currency[] = ["GBP", "EUR"];
const UNITS: DistanceUnit[] = ["mi", "km"];
/** Colours are declared as --vehicle-1..n in styles.css and reused in order. */
const vehicleColor = (i: number) => `var(--vehicle-${(i % VEHICLE_COLOR_COUNT) + 1})`;

const MAIN_ID = "main";

export default function App() {
  const [data, setData] = useState<Data | null>(null);
  const [vehicleId, setVehicleId] = useState("");
  const [profileId, setProfileId] = useState("");
  const currencyHintId = useId();
  const unitHintId = useId();

  useEffect(() => {
    void loadData().then((d) => {
      setData(d);
      setVehicleId(d.vehicles[0].id);
      setProfileId(d.profiles[0].id);
    });
  }, []);

  // Only save once loaded, so the defaults never overwrite what's stored.
  useEffect(() => {
    if (data) void saveData(data);
  }, [data]);

  const updateVehicle = useCallback((id: string, patch: Partial<Vehicle>) => {
    setData((d) => d && { ...d, vehicles: d.vehicles.map((v) => (v.id === id ? { ...v, ...patch } : v)) });
  }, []);
  const updateProfile = useCallback((id: string, patch: Partial<Profile>) => {
    setData((d) => d && { ...d, profiles: d.profiles.map((p) => (p.id === id ? { ...p, ...patch } : p)) });
  }, []);

  const transfer = useDataTransfer({
    data: data ?? { vehicles: [], profiles: [], currency: "GBP", distanceUnit: "mi" },
    onImport: (next) => {
      setData(next);
      if (!next.vehicles.some((v) => v.id === vehicleId)) setVehicleId(next.vehicles[0].id);
      if (!next.profiles.some((p) => p.id === profileId)) setProfileId(next.profiles[0].id);
    },
  });

  if (!data) return null;

  const vehicle = data.vehicles.find((v) => v.id === vehicleId) ?? data.vehicles[0];
  const profile = data.profiles.find((p) => p.id === profileId) ?? data.profiles[0];
  const fmt = makeFmt(data.currency, data.distanceUnit);
  const setCurrency = (currency: Currency) => setData({ ...data, currency });
  const setUnit = (unit: DistanceUnit) => setData(convertData(data, unit));

  const removeVehicle = (id: string) => {
    if (data.vehicles.length <= 1) return;
    setData({ ...data, vehicles: data.vehicles.filter((v) => v.id !== id) });
  };
  const removeProfile = (id: string) => {
    if (data.profiles.length <= 1) return;
    setData({ ...data, profiles: data.profiles.filter((p) => p.id !== id) });
  };

  return (
    <FmtProvider currency={data.currency} unit={data.distanceUnit}>
      <a className="skip-link" href={`#${MAIN_ID}`}>
        {header.skipToContent}
      </a>

      <header className="page-header">
        <div>
          <h1>
            <Logo />
          </h1>
          <p className="tagline">{header.tagline}</p>
        </div>
        <div className="header-toggles">
          <div className="segmented" role="group" aria-label={header.currencyGroup} aria-describedby={currencyHintId}>
            {CURRENCIES.map((c) => (
              <button
                type="button"
                key={c}
                className={data.currency === c ? "active" : ""}
                aria-pressed={data.currency === c}
                aria-label={CURRENCY_LABELS[c]}
                onClick={() => setCurrency(c)}
              >
                {CURRENCY_SYMBOLS[c]}
              </button>
            ))}
          </div>
          <p className="sr-only" id={currencyHintId}>
            {header.currencyHint}
          </p>
          <div className="segmented" role="group" aria-label={header.unitGroup} aria-describedby={unitHintId}>
            {UNITS.map((u) => (
              <button
                type="button"
                key={u}
                className={data.distanceUnit === u ? "active" : ""}
                aria-pressed={data.distanceUnit === u}
                aria-label={UNIT_LABELS[u]}
                onClick={() => setUnit(u)}
              >
                {UNIT_SHORT[u]}
              </button>
            ))}
          </div>
          <p className="sr-only" id={unitHintId}>
            {header.unitHint}
          </p>
          {transfer.controls}
          <ThemeToggle />
        </div>
      </header>

      <main id={MAIN_ID}>
        {transfer.panels}

        <section className="profiles" aria-label={sections.profiles}>
          <h2 className="eyebrow">{sections.profiles}</h2>
          <TabBar
            label={sections.profiles}
            tabs={data.profiles.map((p) => ({ id: p.id, name: p.name, meta: `${fmt.dist(annualDistance(p))}/yr` }))}
            activeId={profile.id}
            addLabel={tabs.addProfile}
            onSelect={setProfileId}
            onRemove={removeProfile}
            onAdd={() => {
              const p = newProfile(data.profiles.length, data.distanceUnit);
              setData({ ...data, profiles: [...data.profiles, p] });
              setProfileId(p.id);
            }}
          />
          <ProfilePanel profile={profile} onChange={(patch) => updateProfile(profile.id, patch)} />
        </section>

        <section aria-label={sections.vehicles}>
          <h2 className="eyebrow">{sections.vehicles}</h2>
          <TabBar
            label={sections.vehicles}
            tabs={data.vehicles.map((v, i) => ({
              id: v.id,
              name: v.name,
              meta: fmt.rate(compute(v, profile, data.distanceUnit).totalPpm),
              color: vehicleColor(i),
            }))}
            activeId={vehicle.id}
            addLabel={tabs.addVehicle}
            onSelect={setVehicleId}
            onRemove={removeVehicle}
            onAdd={() => {
              const v = newVehicle(data.distanceUnit);
              setData({ ...data, vehicles: [...data.vehicles, v] });
              setVehicleId(v.id);
            }}
          />

          <div className="workspace">
            <VehicleForm vehicle={vehicle} profile={profile} onChange={(patch) => updateVehicle(vehicle.id, patch)} />
            <Results vehicle={vehicle} profile={profile} />
          </div>
        </section>

        <Compare vehicles={data.vehicles} profiles={data.profiles} colorOf={vehicleColor} />
      </main>

      <footer>
        <p className="footer-note">{footerNote}</p>
      </footer>
    </FmtProvider>
  );
}
